import { Injectable } from '@angular/core';
import { NavigationDropdown, NavigationItem, NavigationLink, NavigationSubheading } from '../interfaces/navigation-item.interface';
import { Subject } from 'rxjs';
import { QuestionCategory } from '../interfaces/question-category.interface';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {

  items: NavigationItem[] = [];
  private questionCategoryLinks: (NavigationLink | NavigationDropdown)[] = [];

  private _openChangeSubject = new Subject<NavigationDropdown>();
  openChange$ = this._openChangeSubject.asObservable();

  constructor() { }

  triggerOpenChange(item: NavigationDropdown) {
    this._openChangeSubject.next(item);
  }

  isLink(item: NavigationItem): item is NavigationLink {
    return item.type === 'link';
  }

  isDropdown(item: NavigationItem): item is NavigationDropdown {
    return item.type === 'dropdown';
  }

  isSubheading(item: NavigationItem): item is NavigationSubheading {
    return item.type === 'subheading';
  }

  get AdvisorNavigationItems(): NavigationItem[] {
    return [
      {
        type: 'link',
        label: 'Dashboard',
        route: '/',
        icon: 'mat:insights',
        routerLinkActiveOptions: { exact: true }
      },
      {
        type: 'subheading',
        label: 'Manage Users',
        children: [
          {
            type: 'link',
            label: 'Admin',
            route: '/admin',
            icon: 'mat:manage_accounts',
          },
          {
            type: 'link',
            label: 'Clients',
            route: '/clients',
            icon: 'mat:business',
            routerLinkActiveOptions: { exact: false }
          }
        ]
      },
      {
        type: 'subheading',
        label: 'LRPS Management',
        children: [
          {
            type: 'link',
            label: 'Rules',
            route: '/rules',
            icon: 'mat:rule',
            routerLinkActiveOptions: { exact: false }
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Manage Questionnaires',
        children: [
          {
            type: 'dropdown',
            label: 'Questionnaires',
            icon: 'mat:quiz',
            children: [
              ...this.questionCategoryLinks
            ]
          }
        ]
      },
      {
        type: 'link',
        label: 'Assets',
        route: '/assets',
        icon: 'mat:folder',
        routerLinkActiveOptions: { exact: false }
      }
    ];
  }

  get StakeholderNavigationItems(): NavigationItem[] {
    return [
      {
        type: 'link',
        label: 'Dashboard',
        route: '/',
        icon: 'mat:insights',
        routerLinkActiveOptions: { exact: true }
      },
      {
        type: 'subheading',
        label: 'Manage Contributors',
        children: [
          {
            type: 'link',
            label: 'Contributors',
            route: '/contributors',
            icon: 'mat:people',
            routerLinkActiveOptions: { exact: false }
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Manage Use Cases',
        children: [
          {
            type: 'link',
            label: 'Use Cases',
            route: '/use-cases',
            icon: 'mat:dashboard_customize',
            routerLinkActiveOptions: { exact: false }
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Knowledge Base',
        children: [
          {
            type: 'dropdown',
            label: 'Resources',
            icon: 'mat:menu_book',
            children: [
              {
                type: 'link',
                label: 'About',
                route: '/resources/about',
                icon: 'mat:info',
                routerLinkActiveOptions: { exact: false }
              },
              {
                type: 'link',
                label: 'AI Regulatory Framework',
                route: '/resources/ai-regulatory-framework',
                icon: 'mat:gavel',
                routerLinkActiveOptions: { exact: false }
              },
              {
                type: 'link',
                label: '2 + 10 Methodology',
                route: '/resources/methodology',
                icon: 'mat:article',
                routerLinkActiveOptions: { exact: false }
              }
            ]
          }
        ]
      }
    ];
  }

  setNavigationItemsForRole(role: string) {
    if (role.includes('advisor_')) {
      this.items = this.AdvisorNavigationItems;
    } else if (role.includes('stakeholder_')) {
      this.items = this.StakeholderNavigationItems;
    } else {
      this.items = [];
    }
  }

  setQuestionCategoryLinks(categories: QuestionCategory[]) {
    this.questionCategoryLinks = categories.map(category => {
      if (category.slug === 'assets' && category.assets?.length) {
        // Create a dropdown for assets with children
        return {
          type: 'dropdown',
          label: this.toTitleCase(category.name),
          icon: 'mat:category',
          children: category.assets.map(asset => ({
            type: 'link',
            label: this.toTitleCase(asset.name),
            route: `/questionnaires/${category.slug}/${asset.slug}`,
            icon: 'mat:description',
            routerLinkActiveOptions: { exact: false }
          }))
        } as NavigationDropdown;
      } else {
        // Create regular link for other categories
        return {
          type: 'link',
          label: this.toTitleCase(category.name),
          route: `/questionnaires/${category.slug}`,
          icon: 'mat:category',
          routerLinkActiveOptions: { exact: false }
        } as NavigationLink;
      }
    });
  }

  // Helper to change text to title case
  toTitleCase(str: string): string {
    return str.replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  }

}
